/**
 * ProductsCard
 *
 * Dashboard card displaying linked products with scan capabilities.
 * Allows users to add, edit, and manage products from their website.
 */

import React, { useState } from 'react';
import {
    ShoppingBag,
    Plus,
    ExternalLink,
    MoreVertical,
    Edit2,
    Trash2,
    ChevronRight,
    Search,
    Image,
    DollarSign,
    Tag,
    CheckCircle,
    XCircle,
    Sparkles,
    Square,
    CheckSquare
} from 'lucide-react';

const ProductsCard = ({
    products = [],
    onCreateProduct,
    onEditProduct,
    onDeleteProduct,
    onDiscoverProducts,
    onBulkEditVisualNotes,
    loading = false
}) => {
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [selectionMode, setSelectionMode] = useState(false);

    const toggleSelection = (productId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(productId)) {
                next.delete(productId);
            } else {
                next.add(productId);
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === products.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(products.map(p => p.id)));
        }
    };

    const handleBulkEdit = () => {
        const selectedProducts = products.filter(p => selectedIds.has(p.id));
        onBulkEditVisualNotes?.(selectedProducts);
    };

    const exitSelectionMode = () => {
        setSelectionMode(false);
        setSelectedIds(new Set());
    };
    // Sort products: active first, then by name
    const sortedProducts = [...products].sort((a, b) => {
        if (a.isActive !== b.isActive) {
            return a.isActive ? -1 : 1;
        }
        return (a.name || '').localeCompare(b.name || '');
    });

    const formatPrice = (product) => {
        if (!product.price) return null;
        return product.price;
    };

    return (
        <div className="card" style={{ gridColumn: 'span 12' }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-4)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <ShoppingBag size={18} style={{ color: 'var(--color-primary)' }} />
                    <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>Linked Products</span>
                    {products.length > 0 && (
                        <span style={{
                            background: 'var(--color-bg-secondary)',
                            padding: '2px 8px',
                            borderRadius: 'var(--radius-full)',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-secondary)'
                        }}>
                            {products.length}
                        </span>
                    )}
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    {selectionMode ? (
                        <>
                            <button
                                onClick={toggleSelectAll}
                                className="btn btn-ghost btn-sm"
                            >
                                {selectedIds.size === products.length ? (
                                    <><CheckSquare size={14} /> Deselect All</>
                                ) : (
                                    <><Square size={14} /> Select All</>
                                )}
                            </button>
                            {selectedIds.size > 0 && (
                                <button
                                    onClick={handleBulkEdit}
                                    className="btn btn-primary btn-sm"
                                >
                                    <Sparkles size={14} />
                                    Edit Visual Notes ({selectedIds.size})
                                </button>
                            )}
                            <button
                                onClick={exitSelectionMode}
                                className="btn btn-ghost btn-sm"
                            >
                                Cancel
                            </button>
                        </>
                    ) : (
                        <>
                            {products.length > 0 && (
                                <button
                                    onClick={() => setSelectionMode(true)}
                                    className="btn btn-ghost btn-sm"
                                    title="Select products for bulk editing"
                                >
                                    <Sparkles size={14} />
                                    Bulk Edit
                                </button>
                            )}
                            <button
                                onClick={onDiscoverProducts}
                                className="btn btn-ghost btn-sm"
                                title="Scan website for products"
                            >
                                <Search size={14} />
                                Discover
                            </button>
                            <button
                                onClick={onCreateProduct}
                                className="btn btn-primary btn-sm"
                            >
                                <Plus size={14} />
                                Add Product
                            </button>
                        </>
                    )}
                </div>
            </div>

            {loading ? (
                <div style={{
                    padding: 'var(--space-6)',
                    textAlign: 'center',
                    color: 'var(--color-text-secondary)'
                }}>
                    Loading products...
                </div>
            ) : sortedProducts.length === 0 ? (
                <div style={{
                    padding: 'var(--space-6)',
                    textAlign: 'center',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)'
                }}>
                    <ShoppingBag size={32} style={{ color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }} />
                    <p style={{ color: 'var(--color-text-secondary)', margin: '0 0 var(--space-3) 0' }}>
                        No products linked. Add products from your website to feature them in creatives.
                    </p>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'center' }}>
                        <button onClick={onDiscoverProducts} className="btn btn-ghost btn-sm">
                            <Search size={14} />
                            Scan Website
                        </button>
                        <button onClick={onCreateProduct} className="btn btn-primary btn-sm">
                            <Plus size={14} />
                            Add Manually
                        </button>
                    </div>
                </div>
            ) : (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-2)',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    paddingRight: 'var(--space-1)'
                }}>
                    {sortedProducts.map((product) => (
                        <ProductRow
                            key={product.id}
                            product={product}
                            formatPrice={formatPrice}
                            onEdit={() => onEditProduct(product)}
                            onDelete={() => onDeleteProduct(product)}
                            selectionMode={selectionMode}
                            isSelected={selectedIds.has(product.id)}
                            onToggleSelect={() => toggleSelection(product.id)}
                        />
                    ))}
                </div>
            )}

            {/* Info Note */}
            <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-2)',
                padding: 'var(--space-3)',
                background: 'var(--info-50)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                marginTop: 'var(--space-3)',
                border: '1px solid var(--info-200)'
            }}>
                <ShoppingBag size={14} style={{ flexShrink: 0, marginTop: '2px', color: 'var(--info-600)' }} />
                <span style={{ color: 'var(--info-700)' }}>
                    Linked products ensure AI-generated creatives reference real product names, prices, and features instead of making guesses.
                </span>
            </div>
        </div>
    );
};

const ProductRow = ({ product, formatPrice, onEdit, onDelete, selectionMode, isSelected, onToggleSelect }) => {
    const [showMenu, setShowMenu] = React.useState(false);

    const handleClick = () => {
        if (selectionMode) {
            onToggleSelect();
        } else {
            onEdit();
        }
    };

    return (
        <div
            onClick={handleClick}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-3)',
                background: isSelected ? 'var(--primary-50)' : (product.isActive ? 'var(--color-bg-secondary)' : 'var(--color-bg-tertiary)'),
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                transition: 'background var(--transition-fast)',
                borderLeft: `3px solid ${isSelected ? 'var(--color-primary)' : (product.isActive ? 'var(--success-500)' : 'var(--color-border)')}`,
                opacity: product.isActive ? 1 : 0.7
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = isSelected ? 'var(--primary-100)' : 'var(--color-bg-tertiary)'}
            onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? 'var(--primary-50)' : (product.isActive ? 'var(--color-bg-secondary)' : 'var(--color-bg-tertiary)')}
        >
            {/* Selection Checkbox */}
            {selectionMode && (
                <div style={{ flexShrink: 0 }}>
                    {isSelected ? (
                        <CheckSquare size={20} style={{ color: 'var(--color-primary)' }} />
                    ) : (
                        <Square size={20} style={{ color: 'var(--color-text-tertiary)' }} />
                    )}
                </div>
            )}
            {/* Product Image */}
            <div style={{
                width: '48px',
                height: '48px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-bg-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden',
                border: '1px solid var(--color-border)'
            }}>
                {product.imageUrl ? (
                    <img
                        src={product.imageUrl}
                        alt={product.name}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                        }}
                    />
                ) : null}
                <div style={{
                    display: product.imageUrl ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%'
                }}>
                    <Image size={20} style={{ color: 'var(--color-text-tertiary)' }} />
                </div>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {product.name || 'Unnamed Product'}
                    </span>
                    {product.isActive ? (
                        <CheckCircle size={14} style={{ color: 'var(--success-500)', flexShrink: 0 }} />
                    ) : (
                        <XCircle size={14} style={{ color: 'var(--color-text-tertiary)', flexShrink: 0 }} />
                    )}
                </div>
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    {product.category && (
                        <>
                            <Tag size={12} />
                            <span>{product.category}</span>
                            <span style={{ color: 'var(--color-border)' }}>|</span>
                        </>
                    )}
                    {product.productUrl && (
                        <a
                            href={product.productUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: 'var(--color-primary)',
                                textDecoration: 'none'
                            }}
                        >
                            <ExternalLink size={12} />
                            View
                        </a>
                    )}
                </div>
            </div>

            {/* Price */}
            {formatPrice(product) && (
                <div style={{
                    fontWeight: 600,
                    color: 'var(--success-600)',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)'
                }}>
                    {formatPrice(product)}
                </div>
            )}

            {/* Images count */}
            {(() => {
                const imageCount = product.images?.length || (product.imageUrl ? 1 : 0);
                if (imageCount === 0) return null;
                return (
                    <div style={{
                        padding: '2px 8px',
                        background: 'var(--color-bg-primary)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        border: '1px solid var(--color-border)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        <Image size={10} />
                        {imageCount}
                    </div>
                );
            })()}

            {/* Features count */}
            {product.features?.length > 0 && (
                <div style={{
                    padding: '2px 8px',
                    background: 'var(--color-bg-primary)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    border: '1px solid var(--color-border)'
                }}>
                    {product.features.length} features
                </div>
            )}

            {!selectionMode && (
                <>
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowMenu(!showMenu);
                            }}
                            className="btn btn-ghost btn-sm"
                            style={{ padding: 'var(--space-1)' }}
                        >
                            <MoreVertical size={16} />
                        </button>

                        {showMenu && (
                            <>
                                <div
                                    style={{
                                        position: 'fixed',
                                        inset: 0,
                                        zIndex: 99
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowMenu(false);
                                    }}
                                />
                                <div style={{
                                    position: 'absolute',
                                    right: 0,
                                    top: '100%',
                                    background: 'var(--color-bg-primary)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-md)',
                                    boxShadow: 'var(--shadow-lg)',
                                    zIndex: 100,
                                    minWidth: '120px',
                                    overflow: 'hidden'
                                }}>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowMenu(false);
                                            onEdit();
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            width: '100%',
                                            padding: 'var(--space-2) var(--space-3)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: 'var(--font-size-sm)',
                                            color: 'var(--color-text-primary)',
                                            textAlign: 'left'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                    >
                                        <Edit2 size={14} />
                                        Edit
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowMenu(false);
                                            onDelete();
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            width: '100%',
                                            padding: 'var(--space-2) var(--space-3)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: 'var(--font-size-sm)',
                                            color: 'var(--error-600)',
                                            textAlign: 'left'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--error-50)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                    >
                                        <Trash2 size={14} />
                                        Delete
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    <ChevronRight size={16} style={{ color: 'var(--color-text-tertiary)' }} />
                </>
            )}
        </div>
    );
};

export default ProductsCard;
